import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  onFirstNoteLoad,
  onTemplateValueSet,
  overwriteVisitDetails,
  resetProgressNote,
  setActiveSection,
  setCheckCareManagement,
  updateChartErrors,
} from 'src/redux/progressNote/progressNoteSlice'
import { getInitialPayload } from 'src/redux/progressNote/progressNoteThunk'
import Loader from 'src/components/common/Loader/Loader'
import { chartArrayGenerater, createTemplateWithValue, isNoteValide } from 'src/helper/formater'
import {
  billVisitSubmit,
  saveChartProgressNote,
  signChartProgressNote,
} from 'src/helper/backendHelper'
import { toast } from 'react-toastify'
import _ from 'lodash'
import { TAB_OPTIONS } from '../../ProgressNote/progressNoteConstants'
import { COLLECTOR_URL } from '../../../../apiHelper/apiRoute/collector'
import { putCollector } from '../../../../apiHelper/collectorApi'
import AsteriskConfirm from '../../census/components/ConfirmationModals/AsteriskConfirm'
import DynamicSection from '../../ProgressNote/DynamicSection'
import { getSnips } from '../SideEncounterComponents/PanelNoteComponents/panelNoteHelpers'
import {
  clearSidePanel,
  setAutoSaveDraft,
  setPanelChartHelper,
} from '../../../../redux/sidePanel/sidePanelSlice'
import PanelDraftNoteButtons from './PanelDraftNoteButtons'
import {
  billVisitErrorCheck,
  isChargeSigned,
} from '../../../../components/common/shared/sharedEncounterLogic'
import { doesNoteHaveAsterisks } from '../../../../utils/sharedChartFunctions'
import { STATUS_GROUPS, VISIT_STATUS } from '../../../../utils/statusConstants'
import { shouldShowAsteriskWarning } from '../../../../redux/user/userSlice'
import { dateUSFormat } from '../../../../utils/dateTimeUtils'
import { getAge } from '../../../../utils/sharedFunctions'
import { getChartValidations } from '../../../../helper/chartValidationHelper'
import { hasMedicalCoding } from '../../../../components/common/shared/sharedProviderDisplayLogic'
import { ENCOUNTER_TYPES } from '../../sharedConstants'
import { showLongTermCare } from '../../../../utils/sharedFeaturesFunctions'

function findErrorMsg(e) {
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.response?.data?.result ||
    e?.message ||
    e?.result
  )
}

export default function PanelProgressNote({ saveNoteRef }) {
  const dispatch = useDispatch()

  const practice = useSelector((state) => state?.user?.practice)
  const provider = useSelector((state) => state?.user?.provider)
  const roles = useSelector((state) => state?.user?.roles)
  const features = useSelector((state) => state?.user?.features)
  const asteriskIgnorePeriodExpired = useSelector(shouldShowAsteriskWarning)

  const visitStatuses = useSelector((state) => state.census?.visitStatuses) || {}

  // note state selectors
  const visitDetails = useSelector((state) => state.progressNote.visitDetails)
  const selectedTemplate = useSelector((state) => state.progressNote?.selectedTemplate)
  const templateData = useSelector((state) => state.progressNote?.selectedTemplate?.data)
  const selectedSections = useSelector((state) => state.progressNote?.selectedSections)
  const activeSection = useSelector((state) => state?.progressNote?.activeSection)

  const procedureCodesSelected = useSelector(
    (state) => state.sidePanel.panelEncounter?.procedureCodes,
  )
  const diagnosisCodesSelected =
    useSelector((state) => state.sidePanel.panelEncounter?.diagnosisCodes) || []
  const isloading = useSelector((state) => state.progressNote.isloading)
  const callSaveDraft = useSelector((state) => state.progressNote?.isSaveDraft)

  const panelEncounterId = useSelector((state) => state.sidePanel.panelEncounter?._id)
  const snippetList = useSelector((state) => state.sidePanel.panelChartHelpers?.snippets)
  const currentTab =
    useSelector((state) => state.sidePanel.panelChartHelpers?.noteTab) || TAB_OPTIONS.SECTION
  const isTyping = useSelector((state) => state.sidePanel.panelChartHelpers?.isTyping) || true

  const dateOfService = useSelector((state) => state.sidePanel.panelEncounter?.service_date_from)
  const selectedFacilityName = useSelector((state) => state.sidePanel.panelEncounter?.facility_name)
  const selectedPlaceOfService = useSelector(
    (state) => state.sidePanel.panelEncounter?.place_of_service,
  )
  const selectedSharedProvider = useSelector(
    (state) => state.sidePanel.panelEncounter?.incident_to_provider_id,
  )
  const encounterType = useSelector((state) => state.sidePanel.panelEncounter?.encounterType)
  const isCCM = useSelector((state) => state.sidePanel.panelEncounter?.care_coordination)
  const isPCM = useSelector((state) => state.sidePanel.panelEncounter?.principal_care_mngt)
  const autoSaveDraft = useSelector((state) => state.sidePanel.autoSaveDraft)

  const [showAsteriskWarningModal, setShowAsteriskWarningModal] = useState(false)
  const [isFirstAutoFocusSave, setIsFirstAutoFocusSave] = useState(true)
  const [isDraftSaved, setIsDraftSaved] = useState(false)
  const [disableClicks, setDisableClicks] = useState(false)
  const [changesSinceOpen, setChangesSinceOpen] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (autoSaveDraft) {
      dispatch(setAutoSaveDraft(false))
      onSaveDraft()
    }
  }, [autoSaveDraft])

  useEffect(() => {
    if (panelEncounterId) {
      dispatch(getInitialPayload(panelEncounterId))
      getSnips((snips) => dispatch(setPanelChartHelper({ field: 'snippets', value: snips })))
    }
    return () => {
      // move to initial call?
      dispatch(resetProgressNote())
    }
  }, [dispatch, panelEncounterId])

  const setCurrentTab = (newTab) => {
    dispatch(setPanelChartHelper({ field: 'noteTab', value: newTab }))
  }
  const setIsTyping = (typeState) => {
    dispatch(setPanelChartHelper({ field: 'isTyping', value: typeState }))
  }

  useEffect(() => {
    dispatch(setCheckCareManagement({ isCCM, isPCM }))
  }, [dispatch, isCCM, isPCM])

  useEffect(() => {
    if (templateData && selectedSections?.length < 1) {
      let mandatorySections = templateData?.filter(
        (section) => !section?.optional || section?.skip === false,
      )

      // pulled this out so that skipped sections don't get forced to appear
      if (mandatorySections.length === 0) {
        mandatorySections = templateData?.filter((section) => section?.values)
      }

      // if we STILL have no mandatory sections, select all
      // no sections means infinite rerenders
      if (mandatorySections.length === 0) {
        mandatorySections = templateData
      }

      // update the empty note with the valid selected sections
      // (and eventually try to update the left side so its up to date with current dx codes)
      dispatch(onFirstNoteLoad(mandatorySections))
    }
  }, [dispatch, templateData, selectedSections])

  useEffect(() => {
    if (
      isTyping &&
      activeSection?.title === 'Assessment/Plan' &&
      // no value or is empty value
      !Boolean(activeSection.values?.[0]) &&
      diagnosisCodesSelected.length > 0 &&
      activeSection?.sections?.[0]?.options
    ) {
      const section = JSON.parse(JSON.stringify(activeSection))
      section.sections[0].options = diagnosisCodesSelected
      dispatch(onTemplateValueSet(chartArrayGenerater(section)))
    }
  }, [dispatch, activeSection, isTyping])

  useEffect(() => {
    if (callSaveDraft) {
      onSaveDraft()
    }
  }, [callSaveDraft])

  const debounceSaveChartProgressNote = useCallback(
    _.debounce((visitId, progressPayload, noAlert, isPendingReview) => {
      saveChartProgressNote(visitId, progressPayload, 'panelNoteSave')
        .then((res) => {
          if (res?.status === 'success') {
            setIsTyping(true)

            // update with newest chart errors
            dispatch(updateChartErrors(res?.errors))

            // why do we get the image if we do nothing with it?
            // getProgressNoteImage(visitId)
            if (!noAlert) {
              toast.dismiss()
              toast.success('Note Saved...')
            }
            setIsDraftSaved(true)
            if (isPendingReview) {
              onPendingReview()
            }
          } else {
            if (!noAlert) {
              toast.error(`Unable to save draft`)
            }
          }
        })
        .catch((e) => {
          if (!noAlert) {
            toast.error(e?.response?.data?.message || 'Unable to save draft', {
              position: 'bottom-right',
            })
          }
        })
    }, 1000),
    [],
  )

  const onPendingReview = async () => {
    if (panelEncounterId) {
      setDisableClicks(true)
      const url = COLLECTOR_URL.SET_PENDING_REVIEW.replace('{visit_id}', panelEncounterId)
      let data = {
        visit_id: panelEncounterId,
      }
      const res = await putCollector(url, data)
      setDisableClicks(false)
      if (res?.status === 'success') {
        dispatch(clearSidePanel())
      } else {
        console.error(res)
      }
    }
  }

  const onSaveDraft = async (noAlert = false, { isPendingReview } = {}) => {
    if (templateData && selectedSections) {
      let templateWithValue = createTemplateWithValue(selectedTemplate, selectedSections)

      if (panelEncounterId && visitDetails?.patient_id) {
        if (visitDetails?.status === VISIT_STATUS.admit) {
          const url = COLLECTOR_URL.VISIT_STATUS.replace('{visitId}', panelEncounterId)
          await putCollector(
            url,
            // the status we would prefer the visit to update to
            // if its in an 'unseen' state
            { status: VISIT_STATUS.checked_in },
          )
        }

        let progressPayload = {
          ...templateWithValue,
          patient_id: visitDetails?.patient_id,
          encounterType: encounterType,
          initial_encounter: encounterType === ENCOUNTER_TYPES.INITIAL,
          diagnosisCodes: diagnosisCodesSelected,
          procedureCodes: procedureCodesSelected,
          from_v2: true,
          incident_to_provider_id: selectedSharedProvider,
          medical_coding: hasMedicalCoding({ provider }),
          // only add to object if not null
          ...(selectedPlaceOfService && { place_of_service: selectedPlaceOfService }),
        }
        setChangesSinceOpen((prev) => prev + 1)
        debounceSaveChartProgressNote(panelEncounterId, progressPayload, noAlert, isPendingReview)
      }
    }
  }

  // EVIL HACK so we can save on closing panel
  saveNoteRef.current = async () => {
    if (changesSinceOpen > 0) {
      return onSaveDraft()
    }
    return async () => {}
  }

  const onSignAndBill = async ({ ignoreAsteriskWarning = false } = {}) => {
    if (disableClicks) {
      return
    }

    const billHasErrors = billVisitErrorCheck({
      careMngtData: {
        careDx: [visitDetails?.caredx1, visitDetails?.caredx2].filter(Boolean),
        isCCM: visitDetails?.care_coordination,
        isPCM: visitDetails?.principal_care_mngt,
        isLongTermCare: visitDetails?.long_term_care,
        validPos: showLongTermCare(features, visitDetails?.place_of_service),
      },
      chartValidations: await getChartValidations(),
      diagnosisCodes: diagnosisCodesSelected,
      encounterType: visitDetails?.encounterType,
      procedureCodes: procedureCodesSelected,
      practice,
      provider,
      roles,
      service_date_from: visitDetails?.service_date_from,
    })

    if (billHasErrors) {
      // toasts go off in error check
      return
    }

    if (
      !ignoreAsteriskWarning &&
      asteriskIgnorePeriodExpired &&
      doesNoteHaveAsterisks(selectedSections)
    ) {
      return setShowAsteriskWarningModal(true)
    }

    if (!isNoteValide(selectedSections)) {
      toast.error('All required sections (*) must be populated')
      return
    }

    if (templateData && selectedSections) {
      setDisableClicks(true)

      let templateWithValue = createTemplateWithValue(selectedTemplate, selectedSections)
      if (panelEncounterId && visitDetails?.patient_id) {
        if (visitDetails?.status === VISIT_STATUS.admit) {
          const url = COLLECTOR_URL.VISIT_STATUS.replace('{visitId}', panelEncounterId)
          await putCollector(
            url,
            // the status we would prefer the visit to update to
            // if its in an 'unseen' state
            { status: VISIT_STATUS.checked_in },
          )
        }

        try {
          let progressPayload = {
            ...templateWithValue,
            patient_id: visitDetails?.patient_id,
            initial_encounter: encounterType === ENCOUNTER_TYPES.INITIAL,
            encounterType: encounterType,
            diagnosisCodes: diagnosisCodesSelected,
            procedureCodes: procedureCodesSelected,
            from_v2: true,
            incident_to_provider_id: selectedSharedProvider,
            medical_coding: hasMedicalCoding({ provider }),
            // only add to object if not null
            ...(selectedPlaceOfService && { place_of_service: selectedPlaceOfService }),
          }

          delete progressPayload?.note_txt
          const res = await saveChartProgressNote(
            panelEncounterId,
            progressPayload,
            'panelNoteSign',
          )

          if (res?.status === 'success') {
            // update with newest chart errors
            dispatch(updateChartErrors(res?.errors))

            const chargeSigned =
              isChargeSigned({
                ...visitDetails,
                statusGroup: visitStatuses[visitDetails?.status]?.group,
              }) ||
              (visitDetails.status === VISIT_STATUS.bill_pending &&
                visitDetails.rendering_provider_id === provider._id)

            // validate if we can sign the encounter-charge
            if (
              chargeSigned ||
              (new Date(dateUSFormat(dateOfService)) <= new Date() &&
                visitDetails?.status !== VISIT_STATUS.bill_submitted &&
                visitStatuses[visitDetails?.status]?.group !== STATUS_GROUPS.completed)
            ) {
              setIsLoading(true)
              const signBillResp = !chargeSigned
                ? await billVisitSubmit(panelEncounterId)
                : { status: 'success' }

              if (signBillResp?.status === 'success') {
                if (!chargeSigned) {
                  toast.success('Encounter Signed')
                }
                if (signBillResp?.result) {
                  dispatch(overwriteVisitDetails(signBillResp.result))
                }
                const signNoteResp = await signChartProgressNote(panelEncounterId)

                if (signNoteResp?.status === 'success') {
                  toast.success('Note Signed')
                  dispatch(clearSidePanel())
                } else {
                  toast.error(findErrorMsg(signNoteResp) || 'Unable to sign Note')
                }
              } else {
                let errorMsg = findErrorMsg(signBillResp) || 'Unable to sign Encounter'
                toast.error(errorMsg)
              }
            } else {
              toast.error('Encounter and Note cannot be signed')
            }
          } else {
            toast.error(findErrorMsg(res) || 'Error while saving Progress Note')
          }

          setIsLoading(false)
          setDisableClicks(false)
        } catch (e) {
          toast.error(findErrorMsg(e) || 'Error while saving and signing')
          setDisableClicks(false)
          setIsLoading(false)
        }
      }
    }
  }

  const patientDOB =
    visitDetails?.patient_dob && visitDetails?.patient_dob !== 'Invalid date'
      ? dateUSFormat(visitDetails?.patient_dob)
      : ''

  return (
    <div id="PanelProgressNote">
      {(isloading || isLoading) && <Loader />}
      {showAsteriskWarningModal && (
        <AsteriskConfirm
          onConfirm={() => {
            setShowAsteriskWarningModal(false)
            onSignAndBill({ ignoreAsteriskWarning: true })
          }}
          handleClose={() => setShowAsteriskWarningModal(false)}
        />
      )}

      <div
        className="progress-note-wraper"
        style={{ height: !selectedTemplate?.title_print ? '100%' : '93%' }}
      >
        <div className="progress-holder">
          <div className="note-header">
            {visitDetails && (
              <div className="header-row">
                <div className="head-col">
                  <p>SERVICE DATE: {dateOfService || ' '}</p>
                  <p>
                    PATIENT: {visitDetails?.patient_name || ''}{' '}
                    {`(${visitDetails?.gender} | ${patientDOB} | ${getAge(
                      visitDetails?.patient_dob,
                      visitDetails?.service_date_from,
                    )} y/o)`}
                  </p>
                  <p>FACILITY: {selectedFacilityName || ''}</p>
                </div>

                <div className="head-col">
                  <p> {provider?.organization_name ? provider?.organization_name : ''}</p>
                  <p>{provider?.phone ? provider?.phone : ''}</p>
                </div>
              </div>
            )}
            {visitDetails && selectedTemplate?.title_print && (
              <center>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedTemplate?.title_print,
                  }}
                  className="note-title-txt"
                ></div>
              </center>
            )}
          </div>
          <div className="note-body">
            <div className="note-section-holder" key={templateData?.length}>
              <div className="note-section">
                {templateData?.map((section, index) => {
                  let selectedSec = selectedSections?.find(
                    (sec) =>
                      sec?.title === section?.title &&
                      sec?.title !== 'Diagnosis (ICD10)' &&
                      sec?.title !== 'E&M and procedure codes',
                  )
                  return (
                    <React.Fragment key={section?.title + ' fragment'}>
                      {selectedSec && (
                        <DynamicSection
                          section={selectedSec}
                          key={section?.title + index}
                          activeSectionHandle={(section) => {
                            dispatch(setActiveSection(section))
                          }}
                          isTyping={isTyping}
                          setIsTyping={setIsTyping}
                          isDraftSaved={isDraftSaved}
                          setIsDraftSaved={setIsDraftSaved}
                          currentTab={currentTab}
                          setCurrentTab={setCurrentTab}
                          snippets={snippetList || []}
                          onSaveDraft={() => {
                            if (!isFirstAutoFocusSave) {
                              // we do not want the user to open the note,
                              // and have it auto-save
                              onSaveDraft()
                            } else {
                              setIsFirstAutoFocusSave(false)
                            }
                          }}
                        />
                      )}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <PanelDraftNoteButtons onSaveDraft={onSaveDraft} onSignAndBill={onSignAndBill} />
    </div>
  )
}
